import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Permission, ScheduleStatus, WEEKDAY_CODES, dayKeyInTimeZone } from '@construction/shared';
import { drawingsApi, scheduleApi, type ScheduleData } from '@/api/endpoints';
import { Alert, Button, Card, EmptyState, PageHeader, Segmented, Spinner } from '@/components/ui';
import { FullscreenViewer } from '@/features/drawings/FullscreenViewer';
import { LazyMediaViewer } from '@/features/drawings/LazyViewer';
import { SchedulePreview } from '@/features/drawings/SchedulePreview';
import { UploadForm } from '@/features/drawings/UploadForm';
import { GanttChart, type GanttColorMode, type GanttColumns, type GanttScale } from '@/features/schedule/GanttChart';
import { summarizeSchedule } from '@/features/schedule/gantt-layout';
import { ScheduleReview } from '@/features/schedule/ScheduleReview';
import { ScheduleTaskList } from '@/features/schedule/ScheduleTaskList';
import { formatFileSize } from '@/lib/format';
import { useMobileSite } from '@/hooks/useCurrentSite';
import { useErrorMessage } from '@/hooks/useErrorMessage';
import { useMobileShell } from '@/hooks/useMobileShell';
import { DownloadButton } from '@/features/exports/DownloadButton';
import { DrawingDownload } from '@/features/drawings/DrawingDownload';
import { usePermissions } from '@/lib/permissions';

/**
 * 进度表：自己的一页（CR-028），网页端和 App 共用（CR-044）。
 *
 * **客户原话**：「我对于时间表的上传和显示位置不满意。应该单独起一个 tab
 * 上传和查看时间表。」
 *
 * 在这之前它是图纸页顶上的一张卡片（CR-023 的判断是「就一张图、不值得占一格」）。
 * 那个判断错在只看了「有多少内容」，没看**它是拿来干什么的**：
 * 图纸是「我要找某一张」，进度表是「今天该干到哪了」—— 后者是每天要回答一次的问题，
 * 却要先点进一个叫「图纸」的地方才看得到，还跟一堆图纸挤在一起。
 *
 * 上传也一样：换进度表是一件独立的事，不该藏在图纸页里找。
 *
 * **看和换是同一页**（CR-044）：能看的人都进得来，「换一张」按 `schedule.upload` 出现。
 * 改之前 App 里看进度表在「资料」页顶上，这一页只给能换的人 —— 客户原话「不要资料这个合拼页面了」。
 *
 * **甘特图在前，原件在按钮后面**（CR-080）。传上来的 PDF / 图片由能换进度表的人「解析并核对」，
 * 发布之后工地上的人看到的是按数据画的甘特图；还没发布时，大家看到的仍是原件本身 ——
 * 工地上看到的永远对应当前那一版文件，不会出现「原件是新表、甘特图是旧表」。
 */
export function SchedulePage() {
  const { t, i18n } = useTranslation();
  const toMessage = useErrorMessage();
  const { can } = usePermissions();
  const canUpload = can(Permission.SCHEDULE_UPLOAD);
  const canExport = can(Permission.SCHEDULE_EXPORT);
  const isMobileShell = useMobileShell();

  // 网页端工地由路由决定；App 里跟着在班锁定走（约束 9）—— 工人现在也进得来这一页
  const { site, siteId } = useMobileSite();

  const [uploading, setUploading] = useState(false);
  const [viewing, setViewing] = useState(false);
  const [reviewing, setReviewing] = useState(false);

  const schedule = useQuery({
    queryKey: ['schedule', siteId],
    queryFn: () => drawingsApi.schedule(siteId!),
    enabled: siteId !== null,
  });
  const current = schedule.data ?? null;

  // 解析、核对过的数据（CR-080）。key 挂在 ['schedule', siteId] 下面：换了文件（UploadForm 让 ['schedule'] 失效）它跟着刷新
  const data = useQuery({
    queryKey: ['schedule', siteId, 'data'],
    queryFn: () => scheduleApi.get(siteId!),
    enabled: siteId !== null,
  });
  // 只认对应当前这一版文件的那份
  const parsed = data.data && current && data.data.drawingId === current.id ? data.data.schedule : null;
  const isPublished = parsed?.status === ScheduleStatus.PUBLISHED;

  // 历史版本只在确实换过的时候查：第一版没有前身，问了也只会回来它自己
  const versions = useQuery({
    queryKey: ['drawing-versions', current?.id],
    queryFn: () => drawingsApi.versions(current!.id),
    enabled: Boolean(current) && (current?.version ?? 1) > 1,
  });

  const today = dayKeyInTimeZone(new Date(), site?.timezone ?? 'UTC');

  return (
    <>
      <PageHeader
        title={t('nav.schedule')}
        description={site?.name}
        action={
          <div className="flex flex-wrap items-center gap-2">
            {current && isPublished && !reviewing && (
              <Button variant="secondary" onClick={() => setViewing(true)}>
                {t('schedule.original')}
              </Button>
            )}
            {/* 当前这张 / 版本链打包（CR-043）。工地上「按哪版施工的」是会被追责的问题，旧版也要能下 */}
            {canExport && current && <DrawingDownload drawing={current} />}
            {canExport && siteId !== null && current && (current.version ?? 1) > 1 && (
              <DownloadButton
                path={`/sites/${siteId}/exports/schedule`}
                label={t('export.downloadAllVersions')}
                action="schedule.export"
              />
            )}
            {canUpload && isPublished && !reviewing && (
              <Button variant="secondary" onClick={() => setReviewing(true)}>
                {t('schedule.edit')}
              </Button>
            )}
            {canUpload && !reviewing && (
              <Button
                variant={uploading ? 'secondary' : 'primary'}
                onClick={() => setUploading((v) => !v)}
                disabled={siteId === null}
              >
                {uploading ? t('app.cancel') : current ? t('schedule.replace') : t('schedule.upload')}
              </Button>
            )}
          </div>
        }
      />

      {schedule.isError && <Alert>{toMessage(schedule.error)}</Alert>}
      {data.isError && <Alert>{toMessage(data.error)}</Alert>}

      {/*
        换新的走的还是图纸的版本链：`supersedesId` 指向当前这张，
        列表本来就滤掉被替代的版本 —— 「每个工地只有当前一张」
        是现有机制的自然结果，不需要额外的唯一约束（CR-023）。
        传完直接进核对（CR-080）：传新表的人就是最该核对它的人。
      */}
      {uploading && siteId !== null && (
        <Card className="mb-6 p-5">
          <UploadForm
            siteId={siteId}
            mode="SCHEDULE"
            supersedesId={current?.id}
            onDone={() => {
              setUploading(false);
              setReviewing(true);
            }}
          />
        </Card>
      )}

      {(schedule.isLoading || data.isLoading) && (
        <div className="flex justify-center py-16 text-ink-3">
          <Spinner />
        </div>
      )}

      {!schedule.isLoading && !current && !uploading && (
        <EmptyState
          title={t('schedule.empty')}
          hint={canUpload ? t('schedule.emptyHint') : t('schedule.emptyHintWorker')}
        />
      )}

      {/* 核对：key 跟着文件走 —— 刚传完新文件、列表刷新过来时换成新的那张重新解析 */}
      {current && reviewing && canUpload && siteId !== null && !data.isLoading && (
        <ScheduleReview
          key={current.id}
          siteId={siteId}
          drawing={current}
          existing={parsed}
          today={today}
          onOpenOriginal={() => setViewing(true)}
          onClose={() => setReviewing(false)}
        />
      )}

      {current && !reviewing && !data.isLoading && isPublished && parsed && (
        <PublishedSchedule data={parsed} today={today} isMobileShell={isMobileShell} canSeeData={canUpload} />
      )}

      {current && !reviewing && !data.isLoading && !isPublished && (
        <>
          {canUpload && (
            <Card padding="md" className="mb-4 flex flex-wrap items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="font-medium text-ink">{parsed ? t('schedule.draftPending') : t('schedule.notParsed')}</p>
                <p className="mt-0.5 text-body text-ink-3">{t('schedule.notParsedHint')}</p>
              </div>
              <Button onClick={() => setReviewing(true)}>{parsed ? t('schedule.continueReview') : t('schedule.parse')}</Button>
            </Card>
          )}
          <Card className="p-5">
            <SchedulePreview
              drawing={current}
              onOpen={() => setViewing(true)}
              imageClassName="max-h-[60vh] object-contain"
            />

            <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 font-mono text-caption text-ink-3">
              <span>{current.mimeType === 'application/pdf' ? 'PDF' : t('drawings.image')}</span>
              <span className="tabular-nums">{formatFileSize(current.sizeBytes)}</span>
              <span className="tabular-nums">
                {t('schedule.updatedAt', {
                  date: new Date(current.createdAt).toLocaleDateString(i18n.language),
                })}
              </span>
              {versions.data && versions.data.length > 1 && (
                <span>{t('drawings.versionCount', { count: versions.data.length })}</span>
              )}
            </div>
          </Card>
        </>
      )}

      {current && viewing && (
        <FullscreenViewer
          title={current.title}
          meta={t('drawings.version', { version: current.version })}
          onClose={() => setViewing(false)}
        >
          <LazyMediaViewer url={current.url} mimeType={current.mimeType} />
        </FullscreenViewer>
      )}
    </>
  );
}

/**
 * 发布了的进度表（CR-081 按方向稿重排，CR-082 给手机加了列表）：
 * - 电脑：一条统计（开工 / 完工 / 总工期 / 任务构成）→ 一行开关 + 图例 → 甘特图 → 来源说明；
 * - 手机：同一条统计（两列两行）→「列表 / 甘特图」开关，**默认列表**（`ScheduleTaskList`：今天 + 按月）。
 *   甘特图还在开关后面，打开是按天、从今天前三天看起，左边只留名称 + 起止
 *   （CR-080 客户拍板「今天前后三周，可左右滑」）。
 */
function PublishedSchedule({
  data,
  today,
  isMobileShell,
  canSeeData,
}: {
  data: ScheduleData;
  today: string;
  isMobileShell: boolean;
  /** 给 AI / 脚本的那份 JSON 摆出来给能换进度表的人看（接 AI 的是他们） */
  canSeeData: boolean;
}) {
  const { t, i18n } = useTranslation();
  const [scale, setScale] = useState<GanttScale>(isMobileShell ? 'day' : 'week');
  const [colorMode, setColorMode] = useState<GanttColorMode>('type');
  // 手机上默认列表（CR-082）；电脑上只有甘特图
  const [view, setView] = useState<'list' | 'gantt'>(isMobileShell ? 'list' : 'gantt');
  const [columns, setColumns] = useState<Exclude<GanttColumns, 'compact'>>(() =>
    window.innerWidth >= 1600 ? 'full' : 'lite',
  );

  const workdays = data.calendar.workdays.map((code) => WEEKDAY_CODES.indexOf(code as (typeof WEEKDAY_CODES)[number]));
  const summary = summarizeSchedule(data.tasks, workdays);
  const hasSourceColors = data.tasks.some((task) => task.color);
  const lang = i18n.language;
  const weekday = new Intl.DateTimeFormat(lang, { weekday: 'short', timeZone: 'UTC' });
  const monthDay = new Intl.DateTimeFormat(lang, { month: 'numeric', day: 'numeric', timeZone: 'UTC' });
  const ms = (value: string) => Date.parse(`${value}T00:00:00Z`);
  const daysFromToday = (value: string) => Math.round((ms(value) - ms(today)) / 86_400_000);

  const scaleOptions: Array<{ value: GanttScale; label: string }> = [
    { value: 'week', label: t('schedule.gantt.scaleWeek') },
    { value: 'day', label: t('schedule.gantt.scaleDay') },
  ];

  return (
    <div className="space-y-4">
      {summary && (
        <Card className="grid grid-cols-2 lg:grid-cols-4">
          <Kpi
            label={t('schedule.stats.start')}
            value={monthDay.format(ms(summary.start))}
            unit={`${summary.start.slice(0, 4)} ${weekday.format(ms(summary.start))}`}
            sub={
              daysFromToday(summary.start) > 0
                ? t('schedule.stats.startsIn', { count: daysFromToday(summary.start) })
                : t('schedule.stats.startedDays', { count: -daysFromToday(summary.start) + 1 })
            }
          />
          <Kpi
            label={t('schedule.stats.finish')}
            value={monthDay.format(ms(summary.finish))}
            unit={`${summary.finish.slice(0, 4)} ${weekday.format(ms(summary.finish))}`}
            sub={
              daysFromToday(summary.finish) >= 0
                ? t('schedule.stats.daysLeft', { count: daysFromToday(summary.finish) })
                : t('schedule.stats.finished')
            }
          />
          <Kpi
            label={t('schedule.stats.duration')}
            value={String(summary.workdays)}
            unit={t('schedule.stats.workdaysUnit')}
            sub={t('schedule.stats.durationSub', {
              calendar: summary.calendarDays,
              off: data.calendar.nonWorkDates.length,
            })}
          />
          <Kpi
            label={t('schedule.stats.tasks')}
            value={String(data.tasks.length)}
            unit={t('schedule.stats.tasksUnit')}
            sub={t('schedule.stats.tasksValue', {
              work: summary.counts.WORK,
              inspection: summary.counts.INSPECTION,
              nonwork: summary.counts.NONWORK,
            })}
          >
            {/* 构成条：施工 / 检查 / 停工各占多少 */}
            <div className="mt-2 flex h-1.5 gap-0.5 overflow-hidden rounded-full">
              <i className="bg-chart-work" style={{ flex: summary.counts.WORK }} />
              <i className="bg-chart-inspection" style={{ flex: summary.counts.INSPECTION }} />
              <i className="bg-ink-4" style={{ flex: summary.counts.NONWORK }} />
            </div>
          </Kpi>
        </Card>
      )}

      {isMobileShell && (
        <Segmented
          label={t('schedule.viewLabel')}
          hideLabel
          block
          value={view}
          onChange={setView}
          options={[
            { value: 'list', label: t('schedule.viewList') },
            { value: 'gantt', label: t('schedule.viewGantt') },
          ]}
        />
      )}

      {view === 'list' && <ScheduleTaskList data={data} today={today} workdays={workdays} />}

      {view === 'gantt' && (
      <>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <Segmented
          label={t('schedule.gantt.scale')}
          hideLabel
          value={scale}
          onChange={setScale}
          options={isMobileShell ? [...scaleOptions].reverse() : scaleOptions}
        />
        {hasSourceColors && !isMobileShell && (
          <Segmented
            label={t('schedule.gantt.color')}
            hideLabel
            value={colorMode}
            onChange={setColorMode}
            options={[
              { value: 'type', label: t('schedule.gantt.colorType') },
              { value: 'source', label: t('schedule.gantt.colorSource') },
            ]}
          />
        )}
        {!isMobileShell && (
          <Segmented
            label={t('schedule.gantt.columns')}
            hideLabel
            value={columns}
            onChange={setColumns}
            options={[
              { value: 'lite', label: t('schedule.gantt.columnsLite') },
              { value: 'full', label: t('schedule.gantt.columnsFull') },
            ]}
          />
        )}
        {!isMobileShell && (
          <div className="ml-auto">
            <Legend />
          </div>
        )}
      </div>

      <GanttChart
        tasks={data.tasks.map((task) => ({ ...task, key: task.id }))}
        workdays={workdays}
        today={today}
        scale={scale}
        colorMode={colorMode}
        columns={isMobileShell ? 'compact' : columns}
      />
      </>
      )}

      <div className="space-y-1 text-caption leading-relaxed text-ink-3">
        <p>
          {t('schedule.sourceLine', {
            version: data.source.version,
            kind: data.source.mimeType === 'application/pdf' ? 'PDF' : t('drawings.image'),
            how: data.source.parser ? t('schedule.parsedFrom') : t('schedule.enteredByHand'),
            name: data.publishedBy?.name ?? '—',
            date: data.publishedAt ? new Date(data.publishedAt).toLocaleDateString(lang) : '—',
          })}
        </p>
        <p>{t('schedule.workdaysNote')}</p>
        {data.calendar.nonWorkDates.length > 0 && (
          <p>{t('schedule.nonWorkNote', { dates: data.calendar.nonWorkDates.join(', ') })}</p>
        )}
      </div>

      {canSeeData && (
        <details className="rounded-xl border border-rule bg-surface shadow-xs">
          <summary className="flex min-h-touch cursor-pointer items-center px-4 text-body text-ink-2">
            {t('schedule.dataForAi')}
          </summary>
          <p className="px-4 pb-2 text-caption leading-relaxed text-ink-3">{t('schedule.dataForAiHint')}</p>
          <pre className="mx-4 mb-4 max-h-96 overflow-auto rounded-lg bg-surface-2 p-3 font-mono text-caption leading-relaxed text-ink-2">
            {JSON.stringify(data, null, 2)}
          </pre>
        </details>
      )}
    </div>
  );
}

/** 统计条里的一格：小标题、大数字 + 单位、下面一行说明。格与格之间一条竖线（窄屏两列时加横线） */
function Kpi({
  label,
  value,
  unit,
  sub,
  children,
}: {
  label: string;
  value: string;
  unit?: string;
  sub?: string;
  children?: ReactNode;
}) {
  return (
    <div className="min-w-0 border-rule px-4 py-3.5 [&:not(:first-child)]:lg:border-l max-lg:[&:nth-child(-n+2)]:border-b max-lg:[&:nth-child(even)]:border-l">
      <p className="text-caption text-ink-3">{label}</p>
      <p className="mt-1 truncate">
        <span className="text-figure font-semibold tracking-tight tabular-nums text-ink">{value}</span>
        {unit && <span className="ml-1.5 text-caption text-ink-3">{unit}</span>}
      </p>
      {children}
      {sub && <p className="mt-1 truncate text-caption text-ink-3">{sub}</p>}
    </div>
  );
}

function Legend() {
  const { t } = useTranslation();
  const item = (swatch: string, label: string) => (
    <span className="flex items-center gap-1.5">
      <span className={swatch} />
      {label}
    </span>
  );
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-caption text-ink-3">
      {item('h-2 w-3 rounded-sm bg-chart-work', t('schedule.type.WORK'))}
      {item('size-2 rotate-45 rounded-[1px] bg-chart-inspection', t('schedule.type.INSPECTION'))}
      {item(
        'h-2 w-3 rounded-sm border border-ink-3 bg-[repeating-linear-gradient(135deg,var(--ink-3)_0_2px,transparent_2px_5px)]',
        t('schedule.type.NONWORK'),
      )}
      {item('h-3 w-0.5 bg-danger-solid', t('schedule.gantt.todayLegend'))}
    </div>
  );
}
